<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues.ts';
import { useProductListFiltersStore } from '@/stores/productListFilters.ts';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration.ts';
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue';
import { $t } from '@/composables/translations/useTranslate.ts';

const availableValuesStore = usePlayerAvailableValuesStore();
const filtersStore = useProductListFiltersStore();
const configStore = useFilterConfigurationStore();

const expandedGroups = ref<Record<string, boolean>>({});

const availableKeys = computed(() => {
  const dyn = filtersStore.dynamicAvailableKeys;
  const base = new Set<string>(dyn ?? availableValuesStore.accommodationProperties);
  for (const k of filtersStore.selectedAccommodationProperties) base.add(k);
  for (const k of filtersStore.forcedAccommodationProperties) base.add(k);
  return base;
});

const resolvedGroups = computed(() => configStore.resolvedGroups(availableKeys.value));

const isOptionSelected = (compositeKey: string): boolean => {
  if (filtersStore.forcedAccommodationProperties.includes(compositeKey)) return true;
  return filtersStore.selectedAccommodationProperties.includes(compositeKey);
};

const handleCheckboxChange = (compositeKey: string) => {
  if (filtersStore.forcedAccommodationProperties.includes(compositeKey)) return;
  filtersStore.toggleAccommodationProperty(compositeKey);
};

const toggleGroup = (groupId: string) => {
  expandedGroups.value[groupId] = !expandedGroups.value[groupId];
};

const isGroupExpanded = (groupId: string): boolean => {
  return expandedGroups.value[groupId] ?? false;
};

const handleHeaderKeydown = (event: KeyboardEvent, groupId: string) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleGroup(groupId);
  }
};
</script>

<template>
  <div>
    <div
      v-if="availableValuesStore.loading && resolvedGroups.length === 0"
      class="text-gray-500 py-8 text-center"
    >
      {{ $t('ibe.common.loading', 'Loading...') }}
    </div>

    <div v-else-if="resolvedGroups.length === 0" class="text-gray-500 py-8 text-center">
      {{ $t('ibe.filters.no-filters-available', 'No filters available for current results.') }}
    </div>

    <div v-else>
      <div v-for="group in resolvedGroups" :key="group.id" class="border-b border-[#76746E]">
        <!-- Accordion Header -->
        <button
          :id="`accordion-header-${group.id}`"
          type="button"
          class="w-full bg-primary flex items-center justify-between gap-4 px-7.5 py-7.5 cursor-pointer hover:bg-primary/80 transition-all duration-300 ease-in-out"
          :class="{ '!pb-0': isGroupExpanded(group.id) }"
          :aria-expanded="isGroupExpanded(group.id)"
          :aria-controls="`accordion-content-${group.id}`"
          @click="toggleGroup(group.id)"
          @keydown="handleHeaderKeydown($event, group.id)"
        >
          <h4 class="text-body-large text-left">
            {{ $t(group.labelKey, group.id) }}
          </h4>

          <i
            class="icon icon-dropdown-down bg-black w-8 h-8 transition-transform duration-300 ease-in-out"
            :class="isGroupExpanded(group.id) ? 'rotate-180' : ''"
            aria-hidden="true"
          ></i>
        </button>

        <!-- Accordion Content -->
        <Transition
          enter-active-class="transition-all duration-300 ease-in-out"
          enter-from-class="max-h-0 opacity-0"
          enter-to-class="max-h-[1000px] opacity-100"
          leave-active-class="transition-all duration-300 ease-in-out"
          leave-from-class="max-h-[1000px] opacity-100"
          leave-to-class="max-h-0 opacity-0"
        >
          <div
            v-show="isGroupExpanded(group.id)"
            :id="`accordion-content-${group.id}`"
            :aria-labelledby="`accordion-header-${group.id}`"
            role="region"
            class="bg-primary overflow-hidden"
          >
            <div class="px-7.5 pb-0.5 pt-3">
              <div class="space-y-2.5">
                <BaseCheckbox
                  v-for="option in group.options"
                  :id="`filter-${option.groupId}-${option.optionId}`"
                  :key="option.compositeKey"
                  :model-value="isOptionSelected(option.compositeKey)"
                  :disabled="group.disabled"
                  :label="$t(option.optionLabelKey, option.optionId)"
                  @update:model-value="handleCheckboxChange(option.compositeKey)"
                />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
